import React, { useEffect, useState } from 'react';
import { Check, Info } from 'lucide-react';
import { useToast } from '../../../../../components/common/Toast';
import { useAccessTheme } from '../useAccessTheme';
import { EntraDetailGrid, EntraSection } from './EntraWizardUi';
import { getEntraReview, type EntraReview } from './entraConnectionService';

interface EntraReviewStepProps {
  connectionId: string;
}

const policyLabel: Record<EntraReview['sso']['enforcementMode'], string> = {
  optional: 'Optional',
  requiredForDirectoryUsers: 'Required for Entra Users',
  requiredForTenant: 'Required for Tenant',
};

const EntraReviewStep: React.FC<EntraReviewStepProps> = ({ connectionId }) => {
  const t = useAccessTheme();
  const { showToast } = useToast();
  const [review, setReview] = useState<EntraReview | null>(null);

  useEffect(() => {
    let active = true;
    getEntraReview(connectionId)
      .then((data) => {
        if (active) setReview(data);
      })
      .catch((err: Error) => {
        if (active) showToast({ type: 'error', message: err.message });
      });
    return () => {
      active = false;
    };
  }, [connectionId, showToast]);

  if (!review) {
    return <p className={`text-sm ${t.muted}`}>Loading review...</p>;
  }

  const scope = review.sync.userScope === 'selectedGroups' ? 'Users from selected groups' : 'All Users';
  const portalAccess = review.sync.defaultPortalAccessStatus === 'active' ? 'Grant portal access' : 'None';

  return (
    <>
      <div className={`flex items-start gap-2 rounded-lg border px-3 py-2.5 ${t.border}`}>
        <Info className={`w-4 h-4 mt-0.5 shrink-0 ${t.accentText}`} />
        <p className={`text-sm ${t.body}`}>
          Existing Selfic roles, administrator access, and portal permissions will not be overwritten by Microsoft Entra
          synchronization.
        </p>
      </div>

      <EntraSection title="Connection">
        <EntraDetailGrid
          items={[
            {
              label: 'Status',
              value: (
                <span className="inline-flex items-center gap-1">
                  <Check className={`w-4 h-4 ${t.toggleOn}`} /> Microsoft Entra Connected
                </span>
              ),
            },
            { label: 'Organization', value: review.connection.organization || '—' },
            { label: 'Graph Access', value: review.connection.graphAccess },
          ]}
        />
      </EntraSection>

      <EntraSection title="Directory Synchronization">
        <EntraDetailGrid
          items={[
            { label: 'Users', value: review.sync.syncUsers ? 'Enabled' : 'Disabled' },
            { label: 'Groups / Roles', value: review.sync.syncGroups ? 'Enabled' : 'Disabled' },
            { label: 'Group Memberships', value: review.sync.syncGroupMemberships ? 'Enabled' : 'Disabled' },
            { label: 'User Scope', value: scope },
            { label: 'Automatic Sync', value: review.sync.syncEnabled ? 'Enabled' : 'Disabled' },
          ]}
        />
      </EntraSection>

      <EntraSection title="Preview">
        <p className={`text-sm ${t.body}`}>
          {review.preview.usersDiscovered.toLocaleString()} Users
          <br />
          {review.preview.existingUsersMatched.toLocaleString()} Existing Selfic Users
          <br />
          {review.preview.newUsers.toLocaleString()} New Directory Users
          <br />
          {review.preview.groupsDiscovered.toLocaleString()} External Roles
          <br />
          {review.preview.groupMembershipsDiscovered.toLocaleString()} Role Memberships
        </p>
      </EntraSection>

      <EntraSection title="New Users">
        <EntraDetailGrid
          items={[
            { label: 'Portal Access', value: portalAccess },
            { label: 'Primary Selfic Role', value: 'Directory User placeholder' },
            { label: 'External Roles', value: 'From Entra groups' },
          ]}
        />
      </EntraSection>

      <EntraSection title="Existing Users">
        <ul className={`space-y-1 text-sm ${t.body}`}>
          <li>✓ Existing RoleID preserved</li>
          <li>✓ Selfic Admin preserved</li>
          <li>✓ Portal access preserved</li>
          <li>✓ Password/login preserved</li>
          <li>✓ Existing local login preserved</li>
        </ul>
      </EntraSection>

      <EntraSection title="External Roles">
        <EntraDetailGrid
          items={[
            { label: 'Mapping', value: 'Entra Groups → SystemRoles' },
            { label: 'IsImported', value: 'true' },
            { label: 'RoleSource', value: 'ExternalDirectory' },
            { label: 'IsAdmin', value: 'false' },
            { label: 'Membership', value: 'Managed by Entra (UserRoles not written yet)' },
          ]}
        />
      </EntraSection>

      <EntraSection title="Single Sign-On">
        <EntraDetailGrid
          items={[
            { label: 'Microsoft SSO', value: review.sso.isEnabled ? 'Enabled' : 'Disabled' },
            { label: 'Protocol', value: 'OIDC' },
            { label: 'Policy', value: policyLabel[review.sso.enforcementMode] },
            { label: 'Email Linking', value: review.sso.allowEmailLinking ? 'Enabled' : 'Disabled' },
          ]}
        />
      </EntraSection>
    </>
  );
};

export default EntraReviewStep;
